import { Box, MenuItem, Typography } from "@mui/material";
import { partition } from "lodash";
import { ReactElement } from "react";
import { Control } from "react-hook-form";

import { sortContactFields } from "@stw/audience-contact-fields";
import { UpdateSubscriberSchemaType } from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  AudienceFieldData,
  GetAudienceContactFieldsQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import {
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfYesNoSelect } from "~frontend/components/forms/react-hook-form-components/RhfYesNoSelect";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { palette } from "~frontend/theme";
import { STATE_MENU_OPTIONS } from "~frontend/util/stateMenuOptions";

export function ContactFieldsEditor({
  fields,
  editing = false,
  control,
  fieldDefinitions,
  showSaveSuccessful,
}: {
  fields: AudienceFieldData[];
  editing?: boolean;
  control: Control<UpdateSubscriberSchemaType, any, UpdateSubscriberSchemaType>;
  fieldDefinitions: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  showSaveSuccessful: boolean;
}): ReactElement {
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");

  const [customFields, builtInFields] = partition(
    fields,
    f => fieldDefinitions.find(def => def.name === f.name)?.isEditable
  );
  const sortedCustomFields = customFieldsAllowed
    ? sortContactFields(customFields)
    : [];
  const sortedBuiltInFields = sortContactFields(builtInFields);

  return (
    <FlexBox direction="column" gap={3}>
      <Box sx={{ backgroundColor: palette.lightestGray, py: 1, px: 2 }}>
        <TwoColumnDisplay
          labelVariant="body2Bold"
          showTopBorder={false}
          showBottomBorder={false}
          hideDividers={editing}
          allRowSx={{
            py: editing ? "3px !important" : "14px !important",
            color: palette.navy,
          }}
          rows={[...sortedBuiltInFields, ...sortedCustomFields].map(
            ({ name, displayName, value, type }) => {
              const key = name;
              const label = displayName || name;

              const content = () => {
                if (editing) {
                  const defaultParams = {
                    fullWidth: true,
                    name: `fields.${name}` as
                      | "subscriberId"
                      | "fields"
                      | `fields.${string}`,
                    control,
                    sx: { mb: 0, pt: 0 },
                  };

                  switch (type) {
                    case AudienceContactFieldType.String:
                    case AudienceContactFieldType.Zip:
                      return <RhfTextField {...defaultParams} />;
                    case AudienceContactFieldType.Number:
                      return <RhfTextField {...defaultParams} />;
                    case AudienceContactFieldType.Boolean:
                      return <RhfYesNoSelect {...defaultParams} />;
                    case AudienceContactFieldType.State:
                      return (
                        <RhfSelect {...defaultParams} withNoneOption>
                          {STATE_MENU_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </RhfSelect>
                      );
                    case AudienceContactFieldType.Enum:
                      return (
                        <RhfSelect {...defaultParams} withNoneOption>
                          {fieldDefinitions
                            .find(f => f.name === label)
                            ?.typeParameters?.values?.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                        </RhfSelect>
                      );
                    default:
                      return null as never;
                  }
                } else {
                  switch (type) {
                    case AudienceContactFieldType.String:
                    case AudienceContactFieldType.State:
                    case AudienceContactFieldType.Zip:
                      return value || "—";

                    case AudienceContactFieldType.Number:
                      return value ? `${Number(value).toLocaleString()}` : "—";

                    case AudienceContactFieldType.Boolean:
                      return value != null ? (value ? "Yes" : "No") : "—";
                    case AudienceContactFieldType.Enum:
                      return value || "—";
                    default:
                      return null as never;
                  }
                }
              };

              const wrappedContent = editing ? (
                content()
              ) : (
                <Typography variant="body2">{content()}</Typography>
              );

              return {
                key,
                label,
                content: wrappedContent,
              };
            }
          )}
        />
      </Box>

      {showSaveSuccessful && (
        <FlexBox
          direction="column"
          alignItems="center"
          sx={{
            padding: "10px 16px 14px 16px",
            alignSelf: "stretch",
            borderRadius: 0.5,
            background: palette.lightestGreen,
          }}
        >
          <Typography variant="body2">Contact updated successfully.</Typography>
        </FlexBox>
      )}
    </FlexBox>
  );
}
